import {
  updatingClass,
  formatPrice,
  calculateServiceCharges,
  calculatePortFees,
  isCombinationValidForCabin,
} from '../cruise-calculation-common';

export default (nights) => ({
  adults: 2,
  adultsList: 2,
  cabinPrice: {}, // This stores the entire prices object
  cabinPriceSlow: 0,
  cabinPriceUpdating: false,
  child: 0,
  childList: 0,
  height: 0,
  junior: 0,
  juniorList: 0,
  nights: nights,
  selectedCabinName: '', // Defaultní hodnota pro název kajuty
  cabinName: '',
  cabinType: '',
  taxesUpdating: false,
  showSidebar: false,
  cabinDeck: '',

  // Variables to store previous valid values
  validAdults: 2,
  validChild: 0,
  validJunior: 0,

  adultsSlow: 2,
  childSlow: 0,
  juniorSlow: 0,

  showInvalidPassengerWarning: false,

  init() {
    // Watcher for adults
    this.$watch('adults', async (value, oldValue) => {
      if (this.isCombinationValid(value, this.child, this.junior)) {
        setTimeout(() => {
          this.adultsSlow = value;
        }, 200);

        if (oldValue < value) {
          this.adultsList = value;
        } else {
          setTimeout(() => {
            this.adultsList = value;
          }, 200);
        }

        // Trigger animation only if value differs from last valid value
        if (value !== this.validAdults) {
          await this.handleAnimation('taxesUpdating');
        }

        // Update valid value on valid change
        this.validAdults = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.adults = this.validAdults;
        this.adultsSlow = this.validAdults;
      }
    });

    // Watcher for child
    this.$watch('child', async (value, oldValue) => {
      if (this.isCombinationValid(this.adults, value, this.junior)) {
        setTimeout(() => {
          this.childSlow = value;
        }, 200);

        if (oldValue < value) {
          this.childList = value;
        } else {
          setTimeout(() => {
            this.childList = value;
          }, 200);
        }

        if (value !== this.validChild) {
          await this.handleAnimation('taxesUpdating');
        }

        this.validChild = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.child = this.validChild;
        this.childSlow = this.validChild;
      }
    });

    // Watcher for junior
    this.$watch('junior', async (value, oldValue) => {
      if (this.isCombinationValid(this.adults, this.child, value)) {
        setTimeout(() => {
          this.juniorSlow = value;
        }, 200);

        if (oldValue < value) {
          this.juniorList = value;
        } else {
          setTimeout(() => {
            this.juniorList = value;
          }, 200);
        }

        if (value !== this.validJunior) {
          await this.handleAnimation('taxesUpdating');
        }

        this.validJunior = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.junior = this.validJunior;
        this.juniorSlow = this.validJunior;
      }
    });

    // Watcher for the warning display
    this.$watch('showInvalidPassengerWarning', (value) => {
      if (value) {
        setTimeout(() => {
          this.showInvalidPassengerWarning = false;
        }, 7000); // Hide the warning after 10 seconds
      }
    });
  },

  taxesPriceClass() {
    return updatingClass(this.taxesUpdating);
  },

  cabinPriceClass() {
    return updatingClass(this.cabinPriceUpdating);
  },

  // Getter to calculate total service charges
  get totalServiceCharges() {
    return this.calculateServiceCharges(
      this.adultsSlow,
      this.childSlow,
      this.juniorSlow,
      this.nights
    );
  },

  // Method to calculate service charges
  calculateServiceCharges(adults, children, juniors, nights) {
    const { cabinPriceSlow } = this;

    return calculateServiceCharges({
      adults,
      children,
      juniors,
      nights,
      cabinPriceSlow,
    });
  },

  // Getter to calculate total fees (port fees + service charges)
  get totalFees() {
    return this.totalPortFees + this.totalServiceCharges;
  },

  // Getter to calculate total cruise price without fees (ensures it returns a number)
  get cruisePrice() {
    const { adultsSlow, childSlow, juniorSlow, cabinPriceSlow } = this;

    // Calculate the base price for the selected combination
    let key = `price_${adultsSlow}a`;

    if (childSlow > 0) {
      key += `${childSlow}c`;
    }
    if (juniorSlow > 0) {
      key += `${juniorSlow}j`;
    }

    return Number(cabinPriceSlow[key]?.value || 0); // Ensure that a number is returned
  },

  // Getter to calculate final total price (cruisePrice + all fees)
  get totalPrice() {
    return this.cruisePrice + this.totalPortFees + this.totalServiceCharges;
  },

  // Getter to calculate total port fees
  get totalPortFees() {
    return this.calculatePortFees(
      this.adultsSlow,
      this.childSlow,
      this.juniorSlow
    );
  },

  // Method to calculate port fees
  calculatePortFees(adults, children, juniors) {
    const { cabinPriceSlow } = this;

    return calculatePortFees({
      adults,
      children,
      juniors,
      cabinPriceSlow,
    });
  },

  formatPrice(price) {
    const zeroTranslations = {
      included: this.$t('price_included'),
      free: this.$t('free'),
    };

    const object = {
      price,
      zeroTranslations,
    };
    return formatPrice(object);
  },

  async handleAnimation(variable) {
    this[variable] = true;
    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for animation to finish (500ms)
    this[variable] = false;
  },

  // Method to set the price when the cabin changes
  async setCabin(newPrices, cabinName, cabinType) {
    this.showInvalidPassengerWarning = false;
    await this.handleAnimation('cabinPriceUpdating'); // Trigger animation first
    this.cabinPrice = newPrices;
    this.cabinPriceSlow = newPrices;

    // Update the selected cabin name
    this.selectedCabinName = cabinName || this.$t('inside_cabin');

    this.cabinType = cabinType || {
      class: 'fantastica',
      label: 'FANTASTICA Experience',
    };
  },

  // Method to validate the combination of crew
  isCombinationValid(adults, child, junior) {
    const { cabinPriceSlow } = this;

    let key = `price_${adults}a`;
    if (child > 0) {
      key += `${child}c`;
    }
    if (junior > 0) {
      key += `${junior}j`;
    }

    return cabinPriceSlow[key] && cabinPriceSlow[key].value !== null;
  },

  // Method to check if the combination is valid for a specific cabin
  isCombinationValidForCabin(adults, child, junior, prices) {
    const object = {
      adults: adults,
      child: child,
      junior: junior,
      prices: prices,
    };
    return isCombinationValidForCabin(object);
  },

  // Method to set the class for each cabin
  cabinClass(prices) {
    return this.isCombinationValidForCabin(
      this.adults,
      this.child,
      this.junior,
      prices
    )
      ? '' // Valid cabin, no special class
      : 'cruise-calculation-cabin--hidden'; // Invalid cabin, add the disabled class
  },
});
